import {
  Alert,
  Button,
  FormControl,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PrescribeModal from "../Modals/PrescribeModal";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { usePrescription } from "src/context/PrescriptionProvider";
import WhiteCard from "../WhiteCard";
import { useGetPatientSubscriptionsByIdQuery } from "src/api/services/subscriptionsApi";
import { useUpdatePrescriptionMutation } from "src/api/services/prescriptionsApi";
import format from "date-fns/format";
import { MedicationForm, Treatments } from "src/api/types";
import { useGetMedicationsPerProductQuery } from "src/api/services/medicationsApi";
import PrescribeProvider from "src/context/PrescribeProvider";

const mapSubscriptionToDurationInDays: Record<string, number> = {
  "Semaglutide Monthly Membership": 28,
  "Semaglutide Quarterly Membership": 84,
  "Tirzepatide Monthly Membership": 28,
  "Tirzepatide Quarterly Membership": 84,
  "Metformin Monthly Membership": 30,
  "Metformin Quarterly Membership": 90,
  "Semaglutide Monthly Injections": 28,
  "Semaglutide Quarterly Injections": 84,
  "Hair Loss 5 Months Membership": 150,
  "Hair Loss Quarterly Membership": 90,
};

export type MedicationType = {
  pharmacyId: number;
  pharmacyName: string;
  medicationName: string;
  dosage: string | null;
  id: number;
  dispense_unit: string | null;
  dispense_quantity: number;
  strength: string;
  is_glp1: boolean;
  form: MedicationForm;
};

interface Props {
  onSuccess: () => void;
  patientRegion: string | null;
}

const matchForm: Record<string, MedicationForm> = {
  serum: "dropper",
  pill: "oral",
};

const PrescriptionRequest = ({ onSuccess, patientRegion }: Props) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [patientPr, setPatientPr] = useState<number | null>(null);
  const [updatePrescription] = useUpdatePrescriptionMutation();
  const patientId = usePrescription((prescription) => prescription.patient_id);
  const productId = usePrescription((prescription) => prescription.product_id);
  const prescriptionId = usePrescription((prescription) => prescription.id);
  const requestedDosage = usePrescription(
    (prescription) => (prescription.metadata as Record<string, string>)?.dosage,
  );
  const form = usePrescription(
    (prescription) => (prescription.metadata as Record<string, string>)?.form,
  );

  const isCreated = usePrescription(
    (prescription) => prescription.status === "created",
  );
  const isDenied = usePrescription(
    (prescription) => prescription.status === "denied",
  );
  const prescribedAt = usePrescription(
    (prescription) => prescription.prescribed_at,
  );

  const lastUpdatedBy = usePrescription(
    (prescription) =>
      prescription.provider?.profile.first_name +
      " " +
      prescription.provider?.profile.last_name,
  );

  const getPharmacyArgs = useMemo(() => {
    if (productId == null || patientRegion == null) return skipToken;
    return {
      product_id: productId,
      state: patientRegion,
      form,
      active: true,
      pharmacy_status: true,
    };
  }, [form, patientRegion, productId]);

  const { data } = useGetMedicationsPerProductQuery(getPharmacyArgs);

  const { data: subscriptions } =
    useGetPatientSubscriptionsByIdQuery(patientId);

  const subscription = useMemo(() => {
    if (subscriptions == null) return null;
    return subscriptions.find((sub) => sub.product_id === productId) ?? null;
  }, [productId, subscriptions]);

  const alert = useMemo(() => {
    if (requestedDosage != null) {
      return (
        <Alert severity="warning">{`Patient has requested a ${requestedDosage} vial.`}</Alert>
      );
    }

    return null;
  }, [requestedDosage]);

  const medications: MedicationType[] = useMemo(() => {
    if (data == null) return [];

    return data
      .map((m) => ({
        pharmacyId: m.pharmacy_id,
        pharmacyName: m.pharmacy.name,
        medicationName: m.name,
        dosage: m.dosage,
        id: m.id,
        is_glp1:
          m.form === "injectable" && m.treatment.name === Treatments.WeightLoss,
        strength: m.strength,
        dispense_unit: m.dispense_unit,
        dispense_quantity: m.dispense_quantity,
        form: m.form,
      }))
      .flat();
  }, [data]);

  const selectedMedication = useMemo(() => {
    return medications.find((m) => m.id === patientPr);
  }, [medications, patientPr]);

  const requestedMedicationId = useMemo(() => {
    if (requestedDosage == null) return null;

    return medications.find((m) => m.dosage === requestedDosage)?.id;
  }, [medications, requestedDosage]);

  const medicationOptions = useMemo(() => {
    const pharmacyMedications = medications.filter(
      (p) => p.pharmacyId === selectedMedication?.pharmacyId,
    );

    return pharmacyMedications;
  }, [medications, selectedMedication]);

  const medicationIndex = useMemo(() => {
    let index = 0;

    if (requestedDosage != null) {
      index = medications.findIndex((m) => m.dosage === requestedDosage);
    }

    if (form != null) {
      index = medications.findIndex((m) => m.form === matchForm[form]);
    }

    return index < 0 ? 0 : index;
  }, [form, medications, requestedDosage]);

  const handleOpenModal = () => {
    setOpenModal((o) => !o);
  };

  const handleOnChangeVial = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setPatientPr(Number(event.target.value));
    },
    [],
  );

  const durationInDays = useMemo(() => {
    if (subscription == null || subscription.display_name == null) return 28;
    return mapSubscriptionToDurationInDays[subscription?.display_name] ?? 28;
  }, [subscription]);

  const handleUpdateStatus = useCallback(
    async (status: string) => {
      await updatePrescription({
        status: status,
        id: prescriptionId,
      });

      if (status === "denied") {
        onSuccess();
      }
    },
    [onSuccess, prescriptionId, updatePrescription],
  );

  useEffect(() => {
    if (patientPr == null && requestedMedicationId) {
      setPatientPr(requestedMedicationId);
    } else if (patientPr == null && medications[medicationIndex]) {
      setPatientPr(medications[medicationIndex].id);
    }
  }, [
    medicationIndex,
    medications,
    patientPr,
    requestedDosage,
    requestedMedicationId,
  ]);

  return (
    <>
      <WhiteCard
        width="100%"
        sx={{ background: "white" }}
        direction="row"
        gap={1}
        justifyContent="space-between"
      >
        <Stack gap={1}>
          {alert}
          <Typography>{subscription?.display_name}</Typography>
          <FormControl>
            <TextField
              select
              value={patientPr ?? ""}
              onChange={handleOnChangeVial}
              disabled={prescribedAt != null}
              helperText={
                selectedMedication?.strength
                  ? `Formula: ${selectedMedication?.strength}`
                  : null
              }
              sx={{
                width: "fit-content",
                "& .MuiSelect-select": {
                  whiteSpace: "normal",
                },
              }}
            >
              {medications.map((vial) => (
                <MenuItem key={vial.id} value={vial.id}>
                  {`${vial.medicationName} - ${vial.dosage}`}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
          {prescribedAt != null ? (
            <Typography>
              Prescribed on{" "}
              {format(new Date(prescribedAt), `MMM d yyyy 'at' h:mm a`)} by{" "}
              {lastUpdatedBy}
            </Typography>
          ) : null}
          {isDenied ? <Typography>Denied by {lastUpdatedBy}</Typography> : null}
        </Stack>
        <Stack gap={1} flexWrap="wrap">
          <Button onClick={handleOpenModal} disabled={!isCreated}>
            Prescribe
          </Button>
          <Button
            onClick={() => handleUpdateStatus("denied")}
            variant="secondary"
            disabled={!isCreated}
          >
            Deny
          </Button>
          <Button
            onClick={() => handleUpdateStatus("created")}
            variant="alternate"
            disabled={isCreated || !!prescribedAt}
          >
            Requested
          </Button>
        </Stack>
      </WhiteCard>
      {selectedMedication ? (
        <PrescribeProvider region={patientRegion}>
          <PrescribeModal
            daysSupply={durationInDays}
            key={selectedMedication.id}
            open={openModal}
            onClose={setOpenModal}
            medication={selectedMedication}
            onSuccess={onSuccess}
            medicationOptions={medicationOptions}
          />
        </PrescribeProvider>
      ) : null}
    </>
  );
};

export default PrescriptionRequest;
